import { useEffect, useRef, type RefObject } from "react";

/** So nah am Ende gilt die Ansicht noch als „unten“ (dann folgt sie weiter). */
export const NAEHE_PX = 96;

interface MitscrollenOptionen {
  /** Der scrollende Verlauf. Ohne ihn scrollt die Seite, bis `anker` sichtbar ist. */
  bereich?: RefObject<HTMLElement | null>;
  /** Was beim Antworten wächst; ohne Angabe der Bereich selbst. */
  inhalt?: RefObject<HTMLElement | null>;
  /** Bleibt unten sichtbar, wenn die Seite selbst scrollt (Eingabe, Knöpfe). */
  anker?: RefObject<HTMLElement | null>;
  /** Wechselt mit jeder neuen Frage: Dann folgt die Ansicht wieder bis unten. */
  neuStart: unknown;
  /** Aus, solange der Verlauf nicht zu sehen ist (geschlossene Blase). */
  aktiv?: boolean;
}

/**
 * Hält einen Miko-Chat beim Antworten am Ende, solange die Person nicht selbst
 * nach oben gescrollt hat (Frage 133, 07.10.2026). Beobachtet den Inhalt statt
 * der Nachrichten, weil die Schreibanimation (`TypingMarkdown`) nach dem Stream
 * weitertippt; ein Scrollen nur bei neuen Stücken blieb mitten in der Antwort
 * stehen, und die Chatseite scrollte gar nicht mit.
 */
export function useMitscrollen({ bereich, inhalt, anker, neuStart, aktiv = true }: MitscrollenOptionen) {
  const folgen = useRef(true);

  useEffect(() => {
    if (!aktiv) return;
    const scrollEl = bereich?.current ?? null;
    const beobachtet = inhalt?.current ?? scrollEl;
    if (!beobachtet) return;

    const abstand = (): number => {
      if (scrollEl) return scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
      const ende = anker?.current;
      return ende ? ende.getBoundingClientRect().bottom - window.innerHeight : 0;
    };
    const nachUnten = () => {
      if (scrollEl) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
        return;
      }
      if (abstand() > 0) anker?.current?.scrollIntoView({ block: "end" });
    };
    const beimScrollen = () => {
      folgen.current = abstand() < NAEHE_PX;
    };
    const beobachter = new MutationObserver(() => {
      if (folgen.current) nachUnten();
    });
    beobachter.observe(beobachtet, { childList: true, subtree: true, characterData: true });
    // Ohne eigenen Bereich kann jeder Vorfahr scrollen; `capture` hört alle.
    const ziel: EventTarget = scrollEl ?? document;
    ziel.addEventListener("scroll", beimScrollen, { passive: true, capture: !scrollEl });
    return () => {
      beobachter.disconnect();
      ziel.removeEventListener("scroll", beimScrollen, { capture: !scrollEl } as EventListenerOptions);
    };
  }, [aktiv, bereich, inhalt, anker]);

  // Neue Frage: wieder bis unten folgen. Nicht beim ersten Zeichnen, sonst
  // springt die Übersicht beim Laden der Seite zum Kasten.
  const erstesMal = useRef(true);
  useEffect(() => {
    if (erstesMal.current) {
      erstesMal.current = false;
      return;
    }
    if (!aktiv) return;
    folgen.current = true;
    const scrollEl = bereich?.current;
    if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
    else anker?.current?.scrollIntoView({ block: "end" });
  }, [neuStart, aktiv, bereich, anker]);
}
